import { useTranslation } from "react-i18next";
import { ArrowUpLeft } from "lucide-react";
import type { FacilityTreeNode } from "../facility-tree.js";
import { FacilityCountsMeta } from "../FacilityCountsMeta.js";
import { FacilityKebabMenu } from "../FacilityKebabMenu.js";
import { FavoriteStarButton } from "./FavoriteStarButton.js";
import type { KebabProps } from "./useDrilldownActions.js";
import { Button, NavButton } from "@/components/ui/button";
import { ManualIcon } from "@/components/icons";
import { facilityManualsHref } from "./nav-state.js";

/**
 * The currently drilled-into facility, shown as the header of its level (FN / ADR-014; redesigned 2026-10-21). It sits on
 * the raised `card` surface so it reads as the parent of the white child cards below, which hang off it on the list's
 * timeline rail. Controls, left to right:
 * - **Ebene höher** (outline) — go up one level (to the parent, or Home for a root).
 * - **Manuals** (brand orange, like every Manuals button) — THIS facility's manuals; same gate as the cards (direct
 *   permission + has content).
 * - **Kebab** (⋮) — LAST; the host passes `null` outside edit mode. Its Insert-above is suppressed here (a sibling of the
 *   current node belongs to the parent level).
 */
export function DrillLevelHeader({
  node,
  kebab,
  isFavorite,
  onToggleFavorite,
  onReturnToParent,
  onOpenManuals,
}: {
  node: FacilityTreeNode;
  kebab: KebabProps | null;
  isFavorite: boolean;
  onToggleFavorite: (id: number) => void;
  onReturnToParent: () => void;
  onOpenManuals: (id: number) => void;
}) {
  const { t } = useTranslation();
  const canOpenManuals = node.permission !== null && ((node.manualCount ?? 0) > 0 || (node.categoryCount ?? 0) > 0);
  return (
    <div className="bg-card text-card-foreground flex flex-col gap-3 rounded-[14px] p-3 shadow-sm sm:flex-row sm:flex-wrap sm:items-center sm:gap-4 sm:py-[18px] sm:pr-[18px] sm:pl-5">
      <div className="flex min-w-0 flex-col gap-1.5 sm:flex-1">
        {/* Facility name + the favorite star right beside it. */}
        <div className="flex min-w-0 items-center gap-1">
          <h2 className="min-w-0 truncate text-[21px] leading-7 font-bold">{node.name}</h2>
          <FavoriteStarButton name={node.name} isFavorite={isFavorite} onToggle={() => onToggleFavorite(node.id)} className="shrink-0" />
        </div>
        <FacilityCountsMeta
          labelled
          subfacilities={node.childCount}
          categories={node.categoryCount ?? 0}
          manuals={node.manualCount ?? 0}
        />
      </div>
      <div className="flex shrink-0 items-center gap-2 max-sm:flex-wrap">
        <Button
          variant="outline"
          // `dark:border-border`: the outline variant's dark border is `--input`, which equals this header's `--card`
          // surface in dark — the frame would vanish.
          className="dark:border-border h-10 rounded-[10px] px-3.5 text-[13px] font-semibold max-sm:flex-[1_1_40%]"
          aria-label={t("facilitySelection.nav.backToParent", { name: node.name })}
          onPress={onReturnToParent}
        >
          <ArrowUpLeft aria-hidden="true" />
          {t("facilitySelection.nav.levelUp")}
        </Button>
        {canOpenManuals ? (
          <NavButton
            variant="accent"
            className="h-10 rounded-[10px] px-3.5 text-[13px] font-semibold max-sm:flex-[1_1_40%]"
            aria-label={t("facilitySelection.nav.openManuals", { name: node.name })}
            href={facilityManualsHref(node.id)}
            onNavigate={() => onOpenManuals(node.id)}
          >
            <ManualIcon aria-hidden="true" />
            {t("facilitySelection.nav.manualsButton")}
          </NavButton>
        ) : null}
        {/* Kebab LAST → its `right top` popover opens into the gutter, outside the level. */}
        {kebab ? <FacilityKebabMenu {...kebab} showInsertAbove={false} tabbable /> : null}
      </div>
    </div>
  );
}
